import { useEffect, useState } from 'react';
import type { FocusEvent, KeyboardEvent } from 'react';
import { TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import { currentLanguage } from '@/utils/locale';

/**
 * Numeric input that keeps what the user typed until they leave the field
 * (#4097).
 *
 * The old pattern — `type="number"` plus `parseInt(e.target.value) || 1` on
 * every keystroke — turned an emptied field straight back into 0 or 1, so the
 * first digit could never be deleted: replacing "1" with "3" meant typing in
 * front of it and deleting the old digit. This field holds the raw text,
 * reports every complete in-range number while typing (so a Save straight
 * from the keyboard sees it), and on blur parses, rounds and clamps once.
 *
 * Decimal fields accept both `,` and `.`, and show the UI language's
 * separator, so a Finnish user can type `1,5`.
 */
export interface NumberFieldProps
  extends Omit<TextFieldProps, 'value' | 'onChange' | 'type' | 'defaultValue' | 'select'> {
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  /** Arrow-key increment. Defaults to 1. */
  step?: number;
  /** Whole numbers only (default). Set false to allow decimals. */
  integer?: boolean;
  /**
   * When true, an emptied field reports `null`. When false (default), an
   * emptied field falls back on blur to `min` (or 0 without a `min`).
   */
  allowEmpty?: boolean;
}

function decimalSeparator(): string {
  return currentLanguage() === 'en' ? '.' : ',';
}

function formatNumberFieldValue(value: number | null, integer: boolean): string {
  if (value === null || !Number.isFinite(value)) return '';
  const text = String(value);
  return integer ? text : text.replace('.', decimalSeparator());
}

/** `null` for empty or unparseable text; accepts `,` and `.` as decimal separator. */
function parseNumberFieldText(text: string): number | null {
  const trimmed = text.trim().replace(',', '.');
  if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function clamp(value: number, min: number | undefined, max: number | undefined): number {
  let result = value;
  if (min !== undefined && result < min) result = min;
  if (max !== undefined && result > max) result = max;
  return result;
}

/** Decimal step arithmetic (0.1 + 0.2) leaves float noise; trim it to 6 places. */
function tidy(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}

export function NumberField({
  value,
  onChange,
  min,
  max,
  step = 1,
  integer = true,
  allowEmpty = false,
  onBlur,
  onKeyDown,
  slotProps,
  ...textFieldProps
}: NumberFieldProps) {
  const [text, setText] = useState(() => formatNumberFieldValue(value, integer));

  // Follow the value when the parent changes it (load, reset). Values this
  // field reported itself already match the text, so typing is never undone.
  useEffect(() => {
    setText((current) =>
      parseNumberFieldText(current) === value ? current : formatNumberFieldValue(value, integer));
  }, [value, integer]);

  const allowNegative = min === undefined || min < 0;
  const pattern = integer
    ? allowNegative ? /^-?\d*$/ : /^\d*$/
    : allowNegative ? /^-?\d*[.,]?\d*$/ : /^\d*[.,]?\d*$/;

  const isAcceptable = (n: number): boolean =>
    (!integer || Number.isInteger(n))
    && (min === undefined || n >= min)
    && (max === undefined || n <= max);

  const handleChange = (next: string) => {
    if (!pattern.test(next)) return;
    setText(next);
    const parsed = parseNumberFieldText(next);
    if (parsed === null) {
      if (allowEmpty && next.trim() === '' && value !== null) onChange(null);
      return;
    }
    if (isAcceptable(parsed) && parsed !== value) onChange(parsed);
  };

  /** The value the field settles on when the user leaves it. */
  const settle = (raw: string): number | null => {
    const parsed = parseNumberFieldText(raw);
    if (parsed === null) {
      if (allowEmpty) return null;
      return clamp(min ?? 0, min, max);
    }
    const rounded = integer ? Math.round(parsed) : parsed;
    return clamp(rounded, min, max);
  };

  const commit = (next: number | null) => {
    setText(formatNumberFieldValue(next, integer));
    if (next !== value) onChange(next);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    commit(settle(text));
    onBlur?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      const base = parseNumberFieldText(text) ?? value ?? min ?? 0;
      const delta = event.key === 'ArrowUp' ? step : -step;
      commit(clamp(tidy(base + delta), min, max));
    }
    onKeyDown?.(event);
  };

  const htmlInput = (slotProps?.htmlInput ?? {}) as Record<string, unknown>;

  return (
    <TextField
      {...textFieldProps}
      type="text"
      value={text}
      onChange={(event) => handleChange(event.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      slotProps={{
        ...slotProps,
        htmlInput: {
          inputMode: integer ? 'numeric' : 'decimal',
          ...htmlInput,
        },
      }}
    />
  );
}
